import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router";
import { useBreadcrumbs } from "../context/BreadcrumbContext";
import { usePractice } from "../context/PracticeContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { agentsApi } from "../api/agents";
import { graphsApi } from "../api/graphs";
import { patientJourneyApi } from "../api/patientJourney";
import { queryKeys } from "../lib/queryKeys";
import { AgentIcon } from "../components/AgentIconPicker";
import { ArrowRight, ExternalLink } from "lucide-react";
import type { Agent } from "@ligament/shared";
import type { AgentGraph } from "../api/graphs";
import type { PjEscalation } from "../api/patientJourney";

// ─── small inline helpers ─────────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-base font-semibold text-foreground">{children}</h2>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-sm font-semibold text-foreground">{children}</h3>
  );
}

function BodyText({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground leading-relaxed">{children}</p>;
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="bg-muted text-foreground text-xs px-1.5 py-0.5 rounded font-mono">
      {children}
    </code>
  );
}

function ThresholdBadge({
  label,
  type,
}: {
  label: string;
  type: "warning" | "critical";
}) {
  if (type === "critical") {
    return (
      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-destructive/15 text-destructive border border-destructive/25">
        {label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800/40">
      {label}
    </span>
  );
}

// ─── Tab 1: System Blueprint ──────────────────────────────────────────────────

function SystemBlueprintTab() {
  return (
    <div className="space-y-6">
      {/* Three Rules */}
      <div className="space-y-3">
        <SectionHeading>The Three Rules That Must Never Break</SectionHeading>
        <BodyText>
          Every architectural decision is evaluated against these rules. No exceptions.
        </BodyText>
        <div className="space-y-3">
          <div className="rounded-lg border bg-card p-4 space-y-1">
            <SubHeading>Rule 1 — Agents only write to their own folder</SubHeading>
            <BodyText>
              No agent touches another agent's <Code>data/</Code> or <Code>notes/</Code>. An agent
              can read another agent's output only if it is the <Code>data-centralizer</Code>. If an
              agent is deleted or broken, the rest of the system continues running without it.
            </BodyText>
          </div>
          <div className="rounded-lg border bg-card p-4 space-y-1">
            <SubHeading>Rule 2 — The toolbox is the only source for tools</SubHeading>
            <BodyText>
              Agents never include tool code inside their own folder. Tools live in{" "}
              <Code>toolbox/</Code> only. An agent declares which tools it needs in{" "}
              <Code>tools.json</Code>. Tool code executes from the toolbox at runtime — nothing is
              copied into the agent.
            </BodyText>
          </div>
          <div className="rounded-lg border bg-card p-4 space-y-1">
            <SubHeading>Rule 3 — One change in the toolbox propagates to all agents</SubHeading>
            <BodyText>
              Upgrading a tool means changing one file in <Code>toolbox/&lt;tool-slug&gt;/</Code>.
              All agents that reference that tool pick it up on their next run. The upgrade-agent
              audits compatibility first — but the change happens in one place only.
            </BodyText>
          </div>
        </div>
      </div>

      <Separator />

      {/* Agent Types */}
      <div className="space-y-3">
        <SectionHeading>Agent Types</SectionHeading>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {[
            { type: "metric_monitor", desc: "Polls KPIs on a schedule; deterministic + LLM hybrid" },
            { type: "supervisor", desc: "Oversees downstream agents, reviews escalations" },
            { type: "router", desc: "Receives issues and routes them to the right worker" },
            { type: "worker", desc: "Executes a specific remediation or operational task" },
            { type: "computer_use", desc: "Headless browser automation for web tasks" },
            { type: "general", desc: "Platform-level agent with broad read permissions" },
          ].map(({ type, desc }) => (
            <div key={type} className="rounded-md border bg-muted/40 p-3 space-y-1">
              <Code>{type}</Code>
              <p className="text-xs text-muted-foreground mt-1">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Communication Flow */}
      <div className="space-y-3">
        <SectionHeading>Communication Flow</SectionHeading>
        <BodyText>
          The standard run path for the Patient Journey Monitor pipeline:
        </BodyText>
        <div className="rounded-md border bg-muted/30 p-4 font-mono text-xs text-muted-foreground space-y-1 leading-relaxed">
          <div>MetricMonitorRunner (setInterval, 30 min)</div>
          <div className="pl-4">└─► looker-kpi-scanner → fetch KPIs → compare thresholds</div>
          <div className="pl-8">├─► [all healthy] → write state.json + HeartbeatRun → stop</div>
          <div className="pl-8">└─► [breach] → Claude Haiku call (IssueMemory context)</div>
          <div className="pl-16">└─► creates PjEscalation</div>
          <div className="pl-20">└─► queues HeartbeatRuns</div>
          <div className="pl-24">└─► sends board email</div>
        </div>
        <BodyText>
          Phase 1 is zero-LLM-cost. Phase 2 fires only when a KPI breaches a threshold. This keeps
          the per-run Bedrock cost near $0 for healthy runs.
        </BodyText>
      </div>

      <Separator />

      {/* Tech Stack */}
      <div className="space-y-3">
        <SectionHeading>Tech Stack</SectionHeading>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4 font-medium text-foreground">Layer</th>
                <th className="text-left py-2 pr-4 font-medium text-foreground">Technology</th>
                <th className="text-left py-2 font-medium text-foreground">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y text-muted-foreground">
              <tr>
                <td className="py-2 pr-4">Frontend</td>
                <td className="py-2 pr-4">React + Vite / Rspack</td>
                <td className="py-2">Tailwind CSS, React Query, shadcn/ui</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Backend</td>
                <td className="py-2 pr-4">NestJS (Express hybrid)</td>
                <td className="py-2">Better Auth v1.4.18, TypeORM</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Database</td>
                <td className="py-2 pr-4">PostgreSQL 17</td>
                <td className="py-2">AWS RDS, Drizzle migrations</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">AI Models</td>
                <td className="py-2 pr-4">AWS Bedrock</td>
                <td className="py-2">Claude Haiku 4.5 (us.anthropic.claude-haiku-4-5-20251001-v1:0)</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Orchestration</td>
                <td className="py-2 pr-4">Queue-based (work_items)</td>
                <td className="py-2">graph-orchestrator runner + SKIP-LOCKED poller (no LangGraph)</td>
              </tr>
              <tr>
                <td className="py-2 pr-4">Infra</td>
                <td className="py-2 pr-4">AWS ECS Fargate</td>
                <td className="py-2">ECR, Secrets Manager, KMS, Terraform</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Tab 2: Patient Journey Monitor ──────────────────────────────────────────

// Static KPI definitions — thresholds never change at runtime
// NOTE: these KPI field names are illustrative — the live catalog uses the real snow_prd_analytics_db explores (e.g. fct_appointments_table_phi_exclude.percent_appointments_no_show); see /admin/looker-schema for ground truth.
const KPI_DEFINITIONS = [
  {
    key: "initial_no_show_rate",
    label: "Initial Appt No-Show Rate",
    field: "fct_patient_journey_phi_exclude.initial_no_show_rate",
    warning: "> 15%",
    critical: "> 25%",
  },
  {
    key: "initial_noshow_recovery_rate",
    label: "Initial No-Show Recovery Rate",
    field: "dt_patient_journey_recovery.noshow_recovery_rate (stage=initial)",
    warning: "< 40%",
    critical: "< 25%",
  },
  {
    key: "initial_without_us_pct",
    label: "Fulfilled Initial w/ No US Scheduled",
    field: "dt_patient_journey_transitions.initial_without_us_pct",
    warning: "> 20%",
    critical: "> 35%",
  },
  {
    key: "us_no_show_rate",
    label: "US Review No-Show Rate",
    field: "fct_patient_journey_phi_exclude.us_no_show_rate",
    warning: "> 15%",
    critical: "> 25%",
  },
  {
    key: "us_without_procedure_pct",
    label: "Fulfilled US w/ No Procedure Scheduled",
    field: "dt_patient_journey_transitions.us_without_procedure_pct",
    warning: "> 40%",
    critical: "> 50%",
  },
  {
    key: "procedure_no_show_rate",
    label: "Procedure No-Show Rate",
    field: "fct_patient_journey_phi_exclude.procedure_no_show_rate",
    warning: "> 10%",
    critical: "> 20%",
  },
];

function PatientJourneyMonitorTab({
  escalations,
}: {
  escalations: PjEscalation[] | undefined;
}) {
  // Build a map: metricKey → highest severity escalation (latest createdAt wins if tie)
  const liveAlerts = new Map<string, { value: number; severity: string }>();
  if (escalations) {
    for (const esc of escalations) {
      if (esc.alert?.metricKey) {
        const existing = liveAlerts.get(esc.alert.metricKey);
        if (!existing) {
          liveAlerts.set(esc.alert.metricKey, {
            value: esc.alert.metricValueAtAlert,
            severity: esc.alert.severity,
          });
        }
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Agent identity */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <SectionHeading>Patient Journey Monitor</SectionHeading>
          <Badge variant="secondary">metric_monitor</Badge>
          <Badge variant="outline">Claude Haiku 4.5</Badge>
        </div>
        <BodyText>
          Umbrella monitor — checks all 6 KPIs across stages 2, 3, and 5 in a single pass. Runs
          every 30 minutes via the <Code>MetricMonitorRunner</Code> NestJS service. Data is fetched
          from the <Code>snow_prd_analytics_db</Code> / <Code>fct_appointments_table_phi_exclude</Code> Looker explore,
          backed by the <Code>fct_appointments_table_phi_exclude</Code> source table.
        </BodyText>
        <div className="flex flex-wrap gap-2 text-sm">
          <span className="text-muted-foreground">Schedule:</span>
          <Code>every 30 minutes</Code>
          <span className="text-muted-foreground">Explore:</span>
          <Code>fct_appointments_table_phi_exclude</Code>
          <span className="text-muted-foreground">Source table:</span>
          <Code>fct_appointments_table_phi_exclude</Code>
        </div>
      </div>

      <Separator />

      {/* Two-phase execution */}
      <div className="space-y-3">
        <SectionHeading>Two-Phase Execution</SectionHeading>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-lg border bg-card p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline">Phase 1</Badge>
              <span className="text-sm font-medium text-foreground">Deterministic Scan</span>
            </div>
            <BodyText>
              The <Code>looker-kpi-scanner</Code> toolbox tool fetches every configured KPI and
              compares against thresholds. No LLM is called. If all KPIs are within bounds: write{" "}
              <Code>state.json</Code>, log the <Code>HeartbeatRun</Code>, stop.
            </BodyText>
            <p className="text-xs text-muted-foreground">Cost: ~$0 per healthy run</p>
          </div>
          <div className="rounded-lg border bg-card p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Badge variant="outline">Phase 2</Badge>
              <span className="text-sm font-medium text-foreground">LLM Reasoning on Breach</span>
            </div>
            <BodyText>
              On breach, one Bedrock call is made with scan results, agent roster, and the agent's{" "}
              <Code>soul.md</Code>. Claude uses <Code>issue_memory</Code>,{" "}
              <Code>escalation_ladder</Code>, and <Code>audit_log</Code> tools to decide routing and
              create a <Code>PjEscalation</Code>.
            </BodyText>
            <p className="text-xs text-muted-foreground">Fires only when a KPI breaches</p>
          </div>
        </div>
      </div>

      <Separator />

      {/* KPIs */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <SectionHeading>Monitored KPIs</SectionHeading>
          {escalations && liveAlerts.size > 0 && (
            <Badge variant="destructive" className="text-xs">
              {liveAlerts.size} active breach{liveAlerts.size > 1 ? "es" : ""}
            </Badge>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-3 font-medium text-foreground">Metric</th>
                <th className="text-left py-2 pr-3 font-medium text-foreground">Warning</th>
                <th className="text-left py-2 pr-3 font-medium text-foreground">Critical</th>
                <th className="text-left py-2 font-medium text-foreground">Live Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {KPI_DEFINITIONS.map((kpi) => {
                const live = liveAlerts.get(kpi.key);
                const isCritical = live?.severity === "critical";
                const isWarning = live?.severity === "warning";
                return (
                  <tr key={kpi.key}>
                    <td className="py-2 pr-3">
                      <div className="font-medium text-foreground text-xs">{kpi.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        <Code>{kpi.key}</Code>
                      </div>
                    </td>
                    <td className="py-2 pr-3">
                      <ThresholdBadge label={kpi.warning} type="warning" />
                    </td>
                    <td className="py-2 pr-3">
                      <ThresholdBadge label={kpi.critical} type="critical" />
                    </td>
                    <td className="py-2">
                      {isCritical ? (
                        <div className="flex items-center gap-1.5">
                          <ThresholdBadge label="CRITICAL" type="critical" />
                          <span className="text-xs font-mono text-destructive">
                            {(live!.value * 100).toFixed(1)}%
                          </span>
                        </div>
                      ) : isWarning ? (
                        <div className="flex items-center gap-1.5">
                          <ThresholdBadge label="WARNING" type="warning" />
                          <span className="text-xs font-mono text-amber-600 dark:text-amber-400">
                            {(live!.value * 100).toFixed(1)}%
                          </span>
                        </div>
                      ) : (
                        <Badge variant="secondary" className="text-xs">
                          {escalations ? "Healthy" : "Monitoring"}
                        </Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {escalations && liveAlerts.size === 0 && (
          <p className="text-xs text-muted-foreground">
            No active escalations — all KPIs within thresholds.
          </p>
        )}
        {!escalations && (
          <p className="text-xs text-muted-foreground">
            Connect to the sandbox to see live KPI status.
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Tab 3: Stage Agents ──────────────────────────────────────────────────────

// Stage agent name prefixes used to find them in the agents list
const STAGE_AGENT_PREFIXES: Record<"2" | "3" | "5", string> = {
  "2": "Stage 2",
  "3": "Stage 3",
  "5": "Stage 5",
};

function StageAgentsTab({ agents }: { agents: Agent[] | undefined }) {
  // Find real agent IDs by name prefix
  function findAgent(prefix: string): Agent | undefined {
    return agents?.find((a) => a.name.startsWith(prefix));
  }

  const stage2 = findAgent(STAGE_AGENT_PREFIXES["2"]);
  const stage3 = findAgent(STAGE_AGENT_PREFIXES["3"]);
  const stage5 = findAgent(STAGE_AGENT_PREFIXES["5"]);

  function AgentLink({ agent }: { agent: Agent | undefined; label: string }) {
    if (!agent) return null;
    return (
      <Link
        to={`/agents/${agent.id}`}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors no-underline"
      >
        View agent
        <ArrowRight className="h-3 w-3" />
      </Link>
    );
  }

  return (
    <div className="space-y-6">
      <BodyText>
        Stage agents monitor specific stages of the patient journey independently. Stages 1, 4, and
        6 are pending the NextGen EHR data source integration.
      </BodyText>

      {/* Stage 2 */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <SectionHeading>Stage 2 — Initial Appointments</SectionHeading>
          <Badge className="bg-green-600 text-white border-0 text-xs">Active</Badge>
          <AgentLink agent={stage2} label="View agent" />
        </div>
        <BodyText>
          Focused monitor for Stage 2 only. Checks the 3 initial-stage KPIs independently.
          Source explore: <Code>patient_journey</Code>.
        </BodyText>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-3 font-medium text-foreground">Key</th>
                <th className="text-left py-2 pr-3 font-medium text-foreground">LookML Field</th>
                <th className="text-left py-2 pr-3 font-medium text-foreground">Warning</th>
                <th className="text-left py-2 font-medium text-foreground">Critical</th>
              </tr>
            </thead>
            <tbody className="divide-y text-muted-foreground text-xs">
              <tr>
                <td className="py-2 pr-3"><Code>initial_no_show_rate</Code></td>
                <td className="py-2 pr-3"><Code>fct_patient_journey_phi_exclude.initial_no_show_rate</Code></td>
                <td className="py-2 pr-3"><ThresholdBadge label="> 15%" type="warning" /></td>
                <td className="py-2"><ThresholdBadge label="> 25%" type="critical" /></td>
              </tr>
              <tr>
                <td className="py-2 pr-3"><Code>initial_noshow_recovery_rate</Code></td>
                <td className="py-2 pr-3"><Code>dt_patient_journey_recovery.noshow_recovery_rate</Code></td>
                <td className="py-2 pr-3"><ThresholdBadge label="< 40%" type="warning" /></td>
                <td className="py-2"><ThresholdBadge label="< 25%" type="critical" /></td>
              </tr>
              <tr>
                <td className="py-2 pr-3"><Code>initial_without_us_pct</Code></td>
                <td className="py-2 pr-3"><Code>dt_patient_journey_transitions.initial_without_us_pct</Code></td>
                <td className="py-2 pr-3"><ThresholdBadge label="> 20%" type="warning" /></td>
                <td className="py-2"><ThresholdBadge label="> 35%" type="critical" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Separator />

      {/* Stage 3 */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <SectionHeading>Stage 3 — Ultrasound Review</SectionHeading>
          <Badge className="bg-green-600 text-white border-0 text-xs">Active</Badge>
          <AgentLink agent={stage3} label="View agent" />
        </div>
        <BodyText>
          Focused monitor for Stage 3. Includes the critical US-to-Procedure scheduling gap —
          currently the most important KPI in the system.
          Source explore: <Code>patient_journey</Code>.
        </BodyText>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-3 font-medium text-foreground">Key</th>
                <th className="text-left py-2 pr-3 font-medium text-foreground">LookML Field</th>
                <th className="text-left py-2 pr-3 font-medium text-foreground">Warning</th>
                <th className="text-left py-2 font-medium text-foreground">Critical</th>
              </tr>
            </thead>
            <tbody className="divide-y text-muted-foreground text-xs">
              <tr>
                <td className="py-2 pr-3"><Code>us_no_show_rate</Code></td>
                <td className="py-2 pr-3"><Code>fct_patient_journey_phi_exclude.us_no_show_rate</Code></td>
                <td className="py-2 pr-3"><ThresholdBadge label="> 15%" type="warning" /></td>
                <td className="py-2"><ThresholdBadge label="> 25%" type="critical" /></td>
              </tr>
              <tr>
                <td className="py-2 pr-3 font-medium text-foreground">
                  <Code>us_without_procedure_pct</Code>
                  <span className="ml-2"><ThresholdBadge label="CRITICAL NOW" type="critical" /></span>
                </td>
                <td className="py-2 pr-3"><Code>dt_patient_journey_transitions.us_without_procedure_pct</Code></td>
                <td className="py-2 pr-3"><ThresholdBadge label="> 40%" type="warning" /></td>
                <td className="py-2"><ThresholdBadge label="> 50%" type="critical" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Separator />

      {/* Stage 5 */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <SectionHeading>Stage 5 — Procedure / Ablation</SectionHeading>
          <Badge className="bg-green-600 text-white border-0 text-xs">Active</Badge>
          <AgentLink agent={stage5} label="View agent" />
        </div>
        <BodyText>
          Focused monitor for Stage 5.
          Source explore: <Code>patient_journey</Code>.
        </BodyText>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-3 font-medium text-foreground">Key</th>
                <th className="text-left py-2 pr-3 font-medium text-foreground">LookML Field</th>
                <th className="text-left py-2 pr-3 font-medium text-foreground">Warning</th>
                <th className="text-left py-2 font-medium text-foreground">Critical</th>
              </tr>
            </thead>
            <tbody className="divide-y text-muted-foreground text-xs">
              <tr>
                <td className="py-2 pr-3"><Code>procedure_no_show_rate</Code></td>
                <td className="py-2 pr-3"><Code>fct_patient_journey_phi_exclude.procedure_no_show_rate</Code></td>
                <td className="py-2 pr-3"><ThresholdBadge label="> 10%" type="warning" /></td>
                <td className="py-2"><ThresholdBadge label="> 20%" type="critical" /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <Separator />

      {/* Pending stages */}
      <div className="rounded-lg border border-dashed bg-muted/20 p-4 space-y-2">
        <div className="flex items-center gap-2">
          <SubHeading>Stages 1, 4, 6</SubHeading>
          <Badge variant="outline" className="text-xs">Coming Soon</Badge>
        </div>
        <BodyText>
          KPIs for Stages 1 (Referral / Intake), 4 (Authorization), and 6 (Follow-Up) are pending
          the NextGen EHR data source integration. Exploring the migration week of 2026-04-13. Once
          NextGen data lands in Looker, stage agents for these stages will be scaffolded following
          the same two-phase pattern.
        </BodyText>
      </div>
    </div>
  );
}

// ─── Tab 4: Platform Agents ───────────────────────────────────────────────────

// Fallback descriptions for platform agents (keyed by a name fragment)
const PLATFORM_AGENT_CAPABILITY: Record<string, { reads: string; writes: string }> = {
  "Agent Architect": {
    reads: "ARCHITECTURE.md, existing agent folders, NAMING.md",
    writes: "New agent folder: soul.md, config.json, tools.json, CLAUDE.md, README.md, data/, notes/",
  },
  "Data Centralizer": {
    reads: "agents/*/data/state.json, agents/*/data/runs/*.json",
    writes: "agents/data-centralizer/data/ (own store only)",
  },
  "Upgrade Agent": {
    reads: "toolbox/*/tool.json, agents/*/tools.json",
    writes: "Upgrade report → human review required before any toolbox change",
  },
  "Memory Audit Agent": {
    reads: "agents/*/notes/ (all vaults)",
    writes: "agents/memory-audit-agent/data/ (audit summary)",
  },
  "Model Optimizer": {
    reads: "agents/*/data/runs/*.json, cost metrics from Bedrock",
    writes: "Recommendation report → human review required",
  },
};

function PlatformAgentsTab({ agents }: { agents: Agent[] | undefined }) {
  const platformAgents = agents?.filter((a) => {
    const meta = a.metadata as Record<string, unknown> | null | undefined;
    return meta?.isPlatformAgent === true;
  });

  // Fallback static list if API hasn't loaded or no platform agents found
  const staticFallback = [
    { slug: "agent-architect", name: "Agent Architect", type: "general", model: "claude-opus-4-5", icon: "Blueprint", capability: "Scaffolds new agents following the canonical 5-file + data/ + notes/ pattern in a single pass." },
    { slug: "data-centralizer", name: "Data Centralizer", type: "general", model: "claude-3-5-haiku", icon: "Database", capability: "The only agent permitted to read across all agents' data/ folders. Normalizes run results and KPI state into a shared schema." },
    { slug: "upgrade-agent", name: "Upgrade Agent", type: "general", model: "claude-3-5-haiku", icon: "ArrowUp", capability: "Audits toolbox versions vs agent declarations, generates a human-readable upgrade report. Never applies changes automatically." },
    { slug: "memory-audit-agent", name: "Memory Audit Agent", type: "general", model: "claude-3-5-haiku", icon: "Brain", capability: "Reviews agents' notes/ vaults for stale learnings, unresolved errors, and contradictions across Confirmations.md, Corrections.md, and Errors.md." },
    { slug: "model-optimizer", name: "Model Optimizer", type: "general", model: "claude-opus-4-5", icon: "Zap", capability: "Analyzes cost and run-quality data across all agents to recommend model downgrades or upgrades per agent type." },
  ];

  // If we have live platform agents from the API, render them; otherwise fallback
  if (platformAgents && platformAgents.length > 0) {
    return (
      <div className="space-y-6">
        <BodyText>
          Platform agents operate on the agent infrastructure itself, not on patient or operational
          data. They are run manually or on a low-frequency schedule, and always require human review
          before applying changes.
        </BodyText>
        {platformAgents.map((agent, idx) => {
          const meta = agent.metadata as Record<string, unknown> | null | undefined;
          const io = PLATFORM_AGENT_CAPABILITY[agent.name] ?? {
            reads: String(meta?.reads ?? "—"),
            writes: String(meta?.writes ?? "—"),
          };
          return (
            <div key={agent.id}>
              {idx > 0 && <Separator />}
              <div className="space-y-3 pt-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-indigo-100 dark:bg-indigo-900/40">
                    <AgentIcon icon={agent.icon} className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <SectionHeading>{agent.name}</SectionHeading>
                  <Badge variant="secondary">{agent.agentType}</Badge>
                  <Link
                    to={`/agents/${agent.id}`}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors no-underline ml-auto"
                  >
                    View agent <ArrowRight className="h-3 w-3" />
                  </Link>
                </div>
                <BodyText>{String(meta?.capability ?? agent.capabilities ?? "—")}</BodyText>
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="rounded-md bg-muted/40 border p-3 space-y-1">
                    <p className="text-xs font-medium text-foreground">Reads</p>
                    <p className="text-xs text-muted-foreground">{io.reads}</p>
                  </div>
                  <div className="rounded-md bg-muted/40 border p-3 space-y-1">
                    <p className="text-xs font-medium text-foreground">Writes</p>
                    <p className="text-xs text-muted-foreground">{io.writes}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Static fallback (used before API loads or if isPlatformAgent not yet seeded)
  return (
    <div className="space-y-6">
      <BodyText>
        Platform agents operate on the agent infrastructure itself, not on patient or operational
        data. They are run manually or on a low-frequency schedule, and always require human review
        before applying changes.
      </BodyText>
      {staticFallback.map((agent, idx) => {
        const io = PLATFORM_AGENT_CAPABILITY[agent.name];
        return (
          <div key={agent.slug}>
            {idx > 0 && <Separator />}
            <div className="space-y-3 pt-1">
              <div className="flex items-center gap-3 flex-wrap">
                <SectionHeading>{agent.name}</SectionHeading>
                <Badge variant="secondary">{agent.type}</Badge>
                <Badge variant="outline">{agent.model}</Badge>
              </div>
              <BodyText>{agent.capability}</BodyText>
              {io && (
                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="rounded-md bg-muted/40 border p-3 space-y-1">
                    <p className="text-xs font-medium text-foreground">Reads</p>
                    <p className="text-xs text-muted-foreground">{io.reads}</p>
                  </div>
                  <div className="rounded-md bg-muted/40 border p-3 space-y-1">
                    <p className="text-xs font-medium text-foreground">Writes</p>
                    <p className="text-xs text-muted-foreground">{io.writes}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Tab 5: Data Sources ──────────────────────────────────────────────────────

function DataSourcesTab() {
  return (
    <div className="space-y-6">
      {/* Current: NexTech via Looker */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <SectionHeading>Current — NexTech via Looker</SectionHeading>
          <Badge className="bg-green-600 text-white border-0 text-xs">Active</Badge>
        </div>
        <BodyText>
          All current patient journey data flows through Looker, backed by the NexTech EHR in a
          Snowflake analytics database. The platform connects to Looker via a service account using
          OAuth client credentials.
        </BodyText>
        <div className="grid gap-2 sm:grid-cols-3">
          {[
            { label: "Snowflake DB", value: "snow_prd_analytics_db" },
            { label: "Looker Explore", value: "patient_journey" },
            { label: "Source Table", value: "fct_appointments_table_phi_exclude" },
          ].map(({ label, value }) => (
            <div key={label} className="rounded-md border bg-muted/40 p-3 space-y-1">
              <p className="text-xs text-muted-foreground">{label}</p>
              <Code>{value}</Code>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* LookML Files */}
      <div className="space-y-3">
        <SectionHeading>LookML Views</SectionHeading>
        <BodyText>
          Three LookML view files live in <Code>doc/looker/</Code>. They must be added to the
          Looker project alongside the existing <Code>snow_prd_analytics_db</Code> model by a Looker
          developer.
        </BodyText>
        <div className="space-y-2">
          {[
            {
              file: "fct_patient_journey_phi_exclude.view.lkml",
              view: "fct_patient_journey_phi_exclude",
              purpose:
                "Core per-stage metrics — no-show rate, cancellation rate, fulfillment rate for stages 2, 3, 5",
            },
            {
              file: "patient_journey_recovery.view.lkml",
              view: "dt_patient_journey_recovery",
              purpose:
                "Recovery rates (no-show → rescheduled, cancel → rescheduled) per stage",
            },
            {
              file: "patient_journey_transitions.view.lkml",
              view: "dt_patient_journey_transitions",
              purpose:
                "Stage-to-stage scheduling drop-off: did fulfilled patients schedule the next stage?",
            },
          ].map(({ file, view, purpose }) => (
            <div key={file} className="rounded-md border bg-card p-3 space-y-1">
              <div className="flex items-start gap-2 flex-wrap">
                <Code>{file}</Code>
                <span className="text-xs text-muted-foreground">→ view name:</span>
                <Code>{view}</Code>
              </div>
              <p className="text-xs text-muted-foreground">{purpose}</p>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Looker Connection */}
      <div className="space-y-3">
        <SectionHeading>Looker Connection</SectionHeading>
        <BodyText>
          The Looker connection is Bedrock-hosted. Credentials are stored in AWS Secrets Manager
          and injected as environment variables at runtime.
        </BodyText>
        <div className="flex flex-wrap gap-2">
          {["LOOKER_CLIENT_ID", "LOOKER_CLIENT_SECRET", "LOOKER_BASE_URL"].map((env) => (
            <Code key={env}>{env}</Code>
          ))}
        </div>
        <BodyText>
          Access control: the <Code>patient_journey</Code> explore requires{" "}
          <Code>required_access_grants: [phi_access_nextech]</Code>. Users must have{" "}
          <Code>data_role: phi_nextech | phi_full | standard</Code> to query it.
        </BodyText>
      </div>

      <Separator />

      {/* PHI Handling */}
      <div className="space-y-3">
        <SectionHeading>PHI Handling</SectionHeading>
        <BodyText>
          All views used by the patient journey agents carry the <Code>_phi_exclude</Code> suffix in
          their names. This suffix is a naming convention indicating that the view has been
          de-identified — PHI fields such as patient names, DOBs, and MRNs are excluded from the
          Looker field definitions. The <Code>_phi_exclude</Code> naming must be preserved in any
          new views added to this explore.
        </BodyText>
      </div>

      <Separator />

      {/* Upcoming: NextGen */}
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <SectionHeading>Upcoming — NextGen EHR Migration</SectionHeading>
          <Badge variant="outline" className="text-xs">Planned</Badge>
        </div>
        <BodyText>
          VIP Medical Group is migrating from NexTech to NextGen EHR. The engineering team is
          exploring the NextGen data model during the week of 2026-04-13. Once the new data source
          is available in Looker, Stage 1, 4, and 6 agents will be scaffolded and the existing
          stage agents will be updated to reference the NextGen-backed views.
        </BodyText>
        <div className="rounded-lg border border-dashed bg-muted/20 p-4">
          <p className="text-xs text-muted-foreground">
            Impact: Stages 1, 4, 6 KPIs will become available. Existing Stage 2, 3, 5 LookML views
            will need equivalents in the NextGen model. The <Code>toolbox/looker/</Code> tool is
            EHR-agnostic — only the LookML layer changes.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Tab 6: Workflows ─────────────────────────────────────────────────────────

const RUN_PIPELINE_NAME = "Patient Journey Monitor — Run Pipeline";

function WorkflowsTab({ graphs }: { graphs: AgentGraph[] | undefined }) {
  // Find the active Run Pipeline graph for the "Open in Studio" link
  const runPipelineGraph = graphs?.find(
    (g) => g.name === RUN_PIPELINE_NAME && g.status === "active",
  ) ?? graphs?.find((g) => g.name === RUN_PIPELINE_NAME);

  return (
    <div className="space-y-6">
      {/* Active workflow */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <SectionHeading>{RUN_PIPELINE_NAME}</SectionHeading>
          {runPipelineGraph ? (
            <Badge
              className={
                runPipelineGraph.status === "active"
                  ? "bg-green-600 text-white border-0 text-xs"
                  : "text-xs"
              }
            >
              {runPipelineGraph.status === "active" ? "Active" : runPipelineGraph.status}
            </Badge>
          ) : (
            <Badge className="bg-green-600 text-white border-0 text-xs">Active</Badge>
          )}
          {runPipelineGraph && (
            <Link
              to={`/studio/${runPipelineGraph.id}`}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors no-underline ml-auto"
            >
              Open in Studio <ExternalLink className="h-3 w-3" />
            </Link>
          )}
        </div>
        <BodyText>
          The primary operational workflow. Runs the Patient Journey Monitor agent on a 30-minute
          interval via the <Code>MetricMonitorRunner</Code> NestJS service (a <Code>setInterval</Code>{" "}
          loop — not the LangGraph runner). The graph defines two phases with a branch on breach
          detection.
        </BodyText>
      </div>

      {/* Phase 1 */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline">Phase 1</Badge>
          <SubHeading>KPI Scan — Zero LLM Cost</SubHeading>
        </div>
        <div className="rounded-md bg-muted/30 p-3 font-mono text-xs text-muted-foreground space-y-1 leading-relaxed">
          <div>Trigger (every 30 min)</div>
          <div className="pl-4">└─► looker-kpi-scanner (deterministic, no Bedrock call)</div>
          <div className="pl-8">├─► [all healthy] → write state.json → log HeartbeatRun → stop</div>
          <div className="pl-8">└─► [breach detected] → enter Phase 2</div>
        </div>
        <BodyText>
          Tool: <Code>looker-kpi-scanner</Code> from <Code>toolbox/looker/</Code>. Fetches each KPI
          from the <Code>patient_journey</Code> explore, compares against{" "}
          <Code>warningValue</Code> and <Code>criticalValue</Code> thresholds. No LLM is involved.
        </BodyText>
      </div>

      {/* Phase 2 */}
      <div className="rounded-lg border bg-card p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline">Phase 2</Badge>
          <SubHeading>Claude Reasoning on Breach</SubHeading>
        </div>
        <div className="rounded-md bg-muted/30 p-3 font-mono text-xs text-muted-foreground space-y-1 leading-relaxed">
          <div>Breach detected</div>
          <div className="pl-4">└─► Claude Haiku call (Bedrock)</div>
          <div className="pl-8">context: scan results + agent roster + soul.md</div>
          <div className="pl-8">tools: issue_memory, escalation_ladder, audit_log</div>
          <div className="pl-8">└─► creates PjEscalation record</div>
          <div className="pl-12">└─► queues HeartbeatRuns for downstream agents</div>
          <div className="pl-16">└─► sends board email notification</div>
        </div>
        <BodyText>
          Claude uses <Code>issue_memory</Code> to recall how similar KPI breaches were resolved
          previously, enabling context-aware escalation. Routing decisions are Claude's — not
          hardcoded rules.
        </BodyText>
      </div>

      <Separator />

      {/* Archived workflows */}
      <div className="space-y-3">
        <SectionHeading>Archived Workflows</SectionHeading>
        <BodyText>
          Two earlier graph-based approaches have been archived. They remain in the database as
          draft graphs for reference but are no longer executed.
        </BodyText>
        <div className="space-y-2">
          {/* Show real archived graphs from API if available */}
          {graphs
            ?.filter((g) => g.status === "archived" && g.name !== RUN_PIPELINE_NAME)
            .map((g) => (
              <div key={g.id} className="rounded-md border border-dashed bg-muted/20 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <SubHeading>{g.name}</SubHeading>
                  <Badge variant="outline" className="text-xs">Archived</Badge>
                  <Link
                    to={`/studio/${g.id}`}
                    className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors no-underline ml-auto"
                  >
                    View <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>
                {g.description && (
                  <BodyText>{g.description}</BodyText>
                )}
              </div>
            ))}
          {/* Static fallback descriptions for the two known archived graphs */}
          {(!graphs || graphs.filter((g) => g.status === "archived").length === 0) && (
            <>
              <div className="rounded-md border border-dashed bg-muted/20 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <SubHeading>Patient Journey Audit</SubHeading>
                  <Badge variant="outline" className="text-xs">Archived</Badge>
                </div>
                <BodyText>
                  Single-pass sequential workflow. Retired because the non-deterministic LangGraph
                  execution was replaced by the more cost-efficient two-phase{" "}
                  <Code>MetricMonitorRunner</Code> approach.
                </BodyText>
              </div>
              <div className="rounded-md border border-dashed bg-muted/20 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <SubHeading>Patient Journey Audit — Stage View</SubHeading>
                  <Badge variant="outline" className="text-xs">Archived</Badge>
                </div>
                <BodyText>
                  Parallel variant — ran Stage 2, 3, and 5 monitors simultaneously with independent
                  decision branches per stage. Retired for the same reason as the sequential audit.
                </BodyText>
              </div>
            </>
          )}
        </div>
      </div>

      <Separator />

      {/* Execution note */}
      <div className="rounded-lg bg-muted/30 border p-4 space-y-2">
        <SubHeading>Execution Engine</SubHeading>
        <BodyText>
          The Run Pipeline is driven by <Code>MetricMonitorRunner</Code> in{" "}
          <Code>server/src/nest/modules/heartbeat/</Code> — a <Code>setInterval</Code>-based service,
          not the LangGraph runner. The LangGraph runner is used for workflow graphs defined in the
          Workflow Builder UI; the metric monitor pipeline intentionally bypasses it for lower
          latency and cost.
        </BodyText>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function Documentation() {
  const { selectedPracticeId } = usePractice();
  const { setBreadcrumbs } = useBreadcrumbs();

  useEffect(() => {
    setBreadcrumbs([{ label: "Documentation" }]);
  }, [setBreadcrumbs]);

  // ── Live data queries ──────────────────────────────────────────────────────

  const { data: agents } = useQuery({
    queryKey: queryKeys.agents.list(selectedPracticeId!),
    queryFn: () => agentsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: graphs } = useQuery({
    queryKey: ["graphs", selectedPracticeId],
    queryFn: () => graphsApi.list(selectedPracticeId!),
    enabled: !!selectedPracticeId,
  });

  const { data: escalations } = useQuery({
    queryKey: ["pj-escalations", selectedPracticeId, "pending"],
    queryFn: () => patientJourneyApi.getEscalations(selectedPracticeId!, { status: "pending" }),
    enabled: !!selectedPracticeId,
  });

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Page header */}
      <div className="space-y-1">
        <h1 className="text-xl font-semibold text-foreground">System Documentation</h1>
        <p className="text-sm text-muted-foreground">
          Platform architecture, agent specs, and data sources
        </p>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="blueprint">
        <TabsList className="flex-wrap h-auto gap-1 w-full justify-start">
          <TabsTrigger value="blueprint">System Blueprint</TabsTrigger>
          <TabsTrigger value="pj-monitor">
            Patient Journey Monitor
            {escalations && escalations.length > 0 && (
              <span className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-medium text-destructive-foreground">
                {escalations.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="stage-agents">Stage Agents</TabsTrigger>
          <TabsTrigger value="platform-agents">Platform Agents</TabsTrigger>
          <TabsTrigger value="data-sources">Data Sources</TabsTrigger>
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
        </TabsList>

        <TabsContent value="blueprint" className="mt-6">
          <SystemBlueprintTab />
        </TabsContent>

        <TabsContent value="pj-monitor" className="mt-6">
          <PatientJourneyMonitorTab escalations={escalations} />
        </TabsContent>

        <TabsContent value="stage-agents" className="mt-6">
          <StageAgentsTab agents={agents} />
        </TabsContent>

        <TabsContent value="platform-agents" className="mt-6">
          <PlatformAgentsTab agents={agents} />
        </TabsContent>

        <TabsContent value="data-sources" className="mt-6">
          <DataSourcesTab />
        </TabsContent>

        <TabsContent value="workflows" className="mt-6">
          <WorkflowsTab graphs={graphs} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
